import { supabaseClient } from "../auth/supabase.service";
const addSlash = (string = "") => string.endsWith("/") ? string : string + "/";
const removeSlash = (string = "") => string.endsWith("/") ? string.slice(0, -1) : string;

const memo = {
  supaServerList: null,
  supaServerIdToUrlLookup: new Map(),
  supaPageList: null,
  supaACLForCurrentUser: null,
  supaPageDatasourcesByWS: new Map(),
  supaDataserControlListByWS: new Map(),
}


function arrayToLookup(array, {key, valueField = "level", defaultValue = "reader"}){
  const lookup = array.map((d) => {
      const k = key.map(field => d[field]).join(".");
      const v = d[valueField] || defaultValue;
      return [k, v];
    });
  return new Map(lookup);
}

export async function getAccessToken() {
  const {data, error} = await supabaseClient.auth.getSession();
  if (error) return {error: "Failed to figure out access token: " + error};
  return data?.session?.access_token;
}

export async function getSupaACL(refresh = false) {
  if(memo.supaACLForCurrentUser && !refresh) return {ok: memo.supaACLForCurrentUser};
  const { data, error } = await supabaseClient
    .from('acl')
    .select('user_uuid,scope,resource,level,parent')
    .in('scope', ['dataset', 'server', 'site', 'page']);

  if (error) return {error: "Failed to fetch auth scopes: " + error};
  console.log(data)
  memo.supaACLForCurrentUser = arrayToLookup(data, {key: ["user_uuid", "scope", "resource"]});
  return {ok: memo.supaACLForCurrentUser};
}

/**
 * Get all ACL entries for resources where current user is owner
 * Returns flat array that can be grouped by email, scope, resource, etc.
 * Uses RLS policy: "acl read where i own or am myself"
 * @param {boolean} refresh - Force refresh from database
 * @returns {Promise<{ok: Array, error: string}>} Array of ACL entries with user emails
 */
export async function getSupaACLOwnedResources(refresh = false) {
  // Get ACL entries (RLS policy filters to only what user can see - resources where user is owner)
  const { data: aclData, error: aclError } = await supabaseClient
    .from('acl')
    .select('user_uuid, scope, resource, level, parent');
  
  if (aclError) {
    console.error('Error fetching ACL data:', aclError);
    return { error: "Failed to fetch ACL data: " + aclError.message };
  }
  
  // Get all unique user UUIDs (including parents)
  const userUuids = new Set();
  aclData.forEach(row => {
    userUuids.add(row.user_uuid);
    if (row.parent) userUuids.add(row.parent);
  });
  
  // Batch resolve UUIDs to emails via RPC (keeps auth.users protected)
  const { data: emailsData, error: emailsError } = await supabaseClient
    .rpc('resolve_user_emails', { user_uuids: Array.from(userUuids) });
  
  if (emailsError) {
    console.error('Error resolving user emails:', emailsError);
    return { error: "Failed to resolve user emails: " + emailsError.message };
  }
  
  // Create UUID to email lookup
  const uuidToEmail = new Map(emailsData.map(u => [u.id, u.email]));
  
  // Map ACL data to include emails
  const result = aclData.map(row => ({
    email: uuidToEmail.get(row.user_uuid) || row.user_uuid,
    scope: row.scope,
    resource: row.resource,
    level: row.level,
    parent: row.parent ? uuidToEmail.get(row.parent) : null
  }));
  
  return { ok: result };
}

/**
 * Get all ACL lobby entries (access requests) for resources where current user is owner
 * Returns flat array that can be grouped by email, scope, resource, etc.
 * @param {boolean} refresh - Force refresh from database
 * @returns {Promise<{ok: Array, error: string}>} Array of lobby entries with user emails
 */
export async function getSupaACLLobby(refresh = false) {
  // Get ACL lobby entries (RLS policy filters to resources where user is owner)
  const { data: lobbyData, error: lobbyError } = await supabaseClient
    .from('acl_lobby')
    .select('user_uuid, scope, resource, level');
  
  if (lobbyError) {
    console.error('Error fetching ACL lobby data:', lobbyError);
    return { error: "Failed to fetch lobby data: " + lobbyError.message };
  }
  
  // Get all unique user UUIDs
  const userUuids = new Set();
  lobbyData.forEach(row => {
    userUuids.add(row.user_uuid);
  });
  
  // Batch resolve UUIDs to emails via RPC
  const { data: emailsData, error: emailsError } = await supabaseClient
    .rpc('resolve_user_emails', { user_uuids: Array.from(userUuids) });
  
  if (emailsError) {
    console.error('Error resolving user emails:', emailsError);
    return { error: "Failed to resolve user emails: " + emailsError.message };
  }
  
  // Create UUID to email lookup
  const uuidToEmail = new Map(emailsData.map(u => [u.id, u.email]));
  
  // Map lobby data to include emails (keep user_uuid for delete operations)
  const result = lobbyData.map(row => ({
    user_uuid: row.user_uuid,
    email: uuidToEmail.get(row.user_uuid) || row.user_uuid,
    scope: row.scope,
    resource: row.resource,
    level: row.level || null  // Can be null if user didn't specify
  }));
  
  return { ok: result };
}

export async function getSupaPageDatasources(pageId) {
  const { data, error } = await supabaseClient
    .from('pages')
    .select('datasources')
    .eq('id', pageId)
    .single();
  if (error) return { error: "Failed to fetch page datasources: " + error };
  return { ok: data?.datasources || [] };
}

export async function getSupaPageToolset(pageId) {
  const { data, error } = await supabaseClient
    .from('pages')
    .select('toolset')
    .eq('id', pageId)
    .single();
  if (error) return { error: "Failed to fetch page toolset: " + error };
  return { ok: data?.toolset || [] };
}

export async function getSupaPageList(refresh = false) {
  if (memo.supaPageList && !refresh) return {ok: memo.supaPageList};
  const { data, error } = await supabaseClient
    .from('pages')
    .select('*')
    .order('id', { ascending: true });

  if (error) return {error: "Failed to fetch pages from database: " + error};
  memo.supaPageList = data;
  return {ok: memo.supaPageList};
}


export async function getSupaServerList(refresh = false) {
  if (memo.supaServerList && !refresh) return {ok: memo.supaServerList};
  const { data, error } = await supabaseClient
    .from('servers')
    .select('*')
    .neq('id', '__all__'); 
  
  if (error) return {error: "Unable to fetch server list: " + error};
  if(!data.length) return {error: "Server list is empty"};

  memo.supaServerList = data.map(d => ({...d, url: addSlash(d.url)}));
  return {ok: memo.supaServerList};
}

export async function getWaffleServerUrlFromID(id, refresh = false) {
  if (memo.supaServerIdToUrlLookup?.has(id) && !refresh) return {ok: memo.supaServerIdToUrlLookup.get(id)};
  
  const serverList = await getSupaServerList(refresh);
  if (serverList.error) return {error: serverList.error};

  memo.supaServerIdToUrlLookup = new Map(serverList.ok.map(({id, url}) => [id, url]));
  return {ok: memo.supaServerIdToUrlLookup.get(id)};
}

export async function getSupaPageDatasourcesForServer({id, url} = {}, refresh = false){
  if (memo.supaPageDatasourcesByWS?.has(id) && !refresh) return {ok: memo.supaPageDatasourcesByWS.get(id)};

  const { data, error } = await supabaseClient
    .from('pages')
    .select("datasources");
  if (error) return {error: "Failed to fetch waffle datasources: " + error};
  const filtered = data
    .map(m => m.datasources)
    .flat()
    .filter(f => removeSlash(f.reader_properties.url) === removeSlash(url));
  memo.supaPageDatasourcesByWS.set(id, filtered);
  return {ok: memo.supaPageDatasourcesByWS.get(id)};
}

export async function getSupaDatasetControlListForServer({id} = {}, refresh = false){
  if (memo.supaDataserControlListByWS?.has(id) && !refresh) return {ok: memo.supaDataserControlListByWS.get(id)};

  const { data, error } = await supabaseClient
    .from('waffle')
    .select('*')
    .in("server", ["__all__", id])
  if (error) return {error: "Failed to fetch waffle datasources: " + error};

  const mapped = data.map(m => ({
    slug: m.id, 
    server: m.server,
    githubRepoId: m.github_repo_id,
    default_branch: m.default_branch,
    is_private: m.is_private,
    waffleFetcherAppInstallationId: m.waffle_fetcher_app_installation_id,
    branches: m.branches.split(","),
    host: "supa"
  }));
  memo.supaDataserControlListByWS.set(id, mapped);
  return {ok: memo.supaDataserControlListByWS.get(id)};
}

export function deletePageFromDB(pageId) {
  return supabaseClient
    .from('pages')
    .delete()
    .eq('id', pageId);
}

export async function copyTranslationsFromPage(sourcePageId, targetPageId, filters = {}) {
  const { scopes = [], locales = [] } = filters;
  
  // Get target page site and slug for note column
  const { data: targetPage, error: targetPageError } = await supabaseClient
    .from('pages')
    .select('site, slug')
    .eq('id', targetPageId)
    .single();
  
  if (targetPageError) {
    console.error('Error fetching target page:', targetPageError);
    return { error: targetPageError };
  }
  
  const note = `${targetPage.site}/${targetPage.slug}`;
  
  let query = supabaseClient
    .from('translations')
    .select('*')
    .eq('page_id', sourcePageId);
  
  // Apply filters if provided
  if (scopes.length > 0) {
    query = query.in('scope', scopes);
  }
  if (locales.length > 0) {
    query = query.in('locale', locales);
  }
  
  const { data: translations, error: translationsError } = await query;
  
  if (translationsError) {
    console.error('Error fetching translations:', translationsError);
    return { error: translationsError };
  }
  
  if (translations && translations.length > 0) {
    const newTranslations = translations.map(({ id, ...rest }) => ({
      ...rest,
      page_id: targetPageId,
      note
    }));
    
    const { error: insertTransError } = await supabaseClient
      .from('translations')
      .insert(newTranslations);
    
    if (insertTransError) {
      console.error('Error copying translations:', insertTransError);
      return { error: insertTransError };
    }
  } else {
    return { error: `No translations found in source for pageId=${sourcePageId}` };
  }
  
  return { error: null, data: translations };
}

export async function copyConfigsFromPage(sourcePageId, targetPageId, filters = {}) {
  const { types = [], toolIds = [] } = filters;
  
  // Get target page site and slug for note column
  const { data: targetPage, error: targetPageError } = await supabaseClient
    .from('pages')
    .select('site, slug')
    .eq('id', targetPageId)
    .single();
  
  if (targetPageError) {
    console.error('Error fetching target page:', targetPageError);
    return { error: targetPageError };
  }
  
  const note = `${targetPage.site}/${targetPage.slug}`;
  
  let query = supabaseClient
    .from('configs')
    .select('*')
    .eq('page_id', sourcePageId);
  
  // Apply filters if provided
  if (types.length > 0) {
    query = query.in('type', types);
  }
  if (toolIds.length > 0) {
    query = query.in('tool_id', toolIds);
  }
  
  const { data: configs, error: configsError } = await query;
  
  if (configsError) {
    console.error('Error fetching configs:', configsError);
    return { error: configsError };
  }
  
  if (configs && configs.length > 0) {
    const newConfigs = configs.map(({ id, ...rest }) => ({
      ...rest,
      page_id: targetPageId,
      note
    }));
    
    const { error: insertConfigError } = await supabaseClient
      .from('configs')
      .insert(newConfigs);
    
    if (insertConfigError) {
      console.error('Error copying configs:', insertConfigError);
      return { error: insertConfigError };
    }
  } else {
    return { error: `No chart configs found in source for pageId=${sourcePageId}` };
  }
  
  return { error: null, data: configs };
}

/**
 * Get matching locales between source and target pages
 * @param {number} sourcePageId - Source page ID
 * @param {number} targetPageId - Target page ID
 * @returns {Promise<{data: Array<string>, error: any}>} Array of matching locale codes
 */
export async function getMatchingLocales(sourcePageId, targetPageId) {
  const { data: sourceLocales, error: sourceError } = await supabaseClient
    .from('pages')
    .select('locales')
    .eq('id', sourcePageId)
    .single();
  
  if (sourceError) return { error: sourceError, data: null };
  
  const { data: targetLocales, error: targetError } = await supabaseClient
    .from('pages')
    .select('locales')
    .eq('id', targetPageId)
    .single();
  
  if (targetError) return { error: targetError, data: null };
  
  const sourceSet = new Set(sourceLocales?.locales || []);
  const targetSet = new Set(targetLocales?.locales || []);
  const matching = [...sourceSet].filter(locale => targetSet.has(locale));
  
  return { error: null, data: matching };
}

/**
 * Get matching tool IDs between source and target pages
 * @param {number} sourcePageId - Source page ID
 * @param {number} targetPageId - Target page ID
 * @returns {Promise<{data: Array<string>, error: any}>} Array of matching tool IDs
 */
export async function getMatchingToolIds(sourcePageId, targetPageId) {
  const { data: sourceToolset, error: sourceError } = await supabaseClient
    .from('pages')
    .select('toolset')
    .eq('id', sourcePageId)
    .single();
  
  if (sourceError) return { error: sourceError, data: null };
  
  const { data: targetToolset, error: targetError } = await supabaseClient
    .from('pages')
    .select('toolset')
    .eq('id', targetPageId)
    .single();
  
  if (targetError) return { error: targetError, data: null };
  
  const sourceIds = (sourceToolset?.toolset || []).map(t => t.id);
  const targetIds = (targetToolset?.toolset || []).map(t => t.id);
  const sourceSet = new Set(sourceIds);
  const targetSet = new Set(targetIds);
  const matching = [...sourceSet].filter(id => targetSet.has(id));
  
  return { error: null, data: matching };
}

/**
 * Create a new page by copying from an existing homepage
 * @param {string} slug - The slug for the new page
 * @param {string} site - The site for the new page
 * @param {Object} homepage - The homepage object to copy from
 * @returns {Promise<{data: Object, error: any}>} The newly created page data or error
 */
export async function createPageFromHomepage(slug, site, homepage) {
  if (!homepage) {
    return { error: `Cannot find a homepage for the site ${site}`, data: null };
  }

  const { id: homepageId, ...homepageWithoutId } = homepage;
  const { data, error } = await supabaseClient
    .from('pages')
    .insert([{ ...homepageWithoutId, slug }])
    .select()
    .single();
  
  if (error) {
    console.error('Error creating page:', error);
    return { error, data: null };
  }
  
  const newPageId = data.id;
  
  // Copy translations and configs from homepage
  const translationsResult = await copyTranslationsFromPage(homepageId, newPageId);
  const configsResult = await copyConfigsFromPage(homepageId, newPageId);
  
  return {
    error: null,
    data: {
      page: data,
      translationsCopied: translationsResult.data?.length || 0,
      configsCopied: configsResult.data?.length || 0,
      translationsError: translationsResult.error,
      configsError: configsResult.error
    }
  };
}

/**
 * Copy page data (translations and configs) from one existing page to another
 * @param {number} sourcePageId - The source page ID to copy from
 * @param {number} targetPageId - The target page ID to copy to
 * @param {Object} options - Options for copying
 * @param {boolean} options.replaceExisting - If true, delete existing data before copying
 * @param {Array<string>} options.pageFields - Array of field names from pages table to copy (e.g., ['toolset', 'datasources'])
 * @param {boolean} options.copyTranslations - If true, copy translations (default: false)
 * @param {boolean} options.copyConfigs - If true, copy configs (default: false)
 * @param {Object} options.translationFilters - Filters for translations { scopes: [], locales: [] }
 * @param {Object} options.configFilters - Filters for configs { types: [], toolIds: [] }
 * @returns {Promise<{data: Object, error: any}>} Summary of copy operation
 */
export async function copyPageData(sourcePageId, targetPageId, { 
  replaceExisting = false, 
  pageFields = [], 
  copyTranslations = false, 
  copyConfigs = false,
  translationFilters = {},
  configFilters = {}
} = {}) {
  // Copy page table fields if specified
  let pageFieldsCopied = [];
  if (pageFields && pageFields.length > 0) {
    // Fetch source page data
    const { data: sourcePage, error: fetchError } = await supabaseClient
      .from('pages')
      .select(pageFields.join(','))
      .eq('id', sourcePageId)
      .single();
    
    if (fetchError) {
      console.error('Error fetching source page fields:', fetchError);
      return { error: fetchError, data: null };
    }
    
    // Prepare update object
    const updateData = {};
    pageFields.forEach(field => {
      if (sourcePage[field] !== undefined) {
        updateData[field] = sourcePage[field];
        pageFieldsCopied.push(field);
      }
    });
    
    // Update target page with source page fields
    if (Object.keys(updateData).length > 0) {
      const { error: updateError } = await supabaseClient
        .from('pages')
        .update(updateData)
        .eq('id', targetPageId);
      
      if (updateError) {
        console.error('Error updating target page fields:', updateError);
        return { error: updateError, data: null };
      }
    }
  }
  
  // If replaceExisting, delete existing translations and configs
  if (replaceExisting) {
    if (copyTranslations) {
      const { error: deleteTransError } = await supabaseClient
        .from('translations')
        .delete()
        .eq('page_id', targetPageId);
      
      if (deleteTransError) {
        console.error('Error deleting existing translations:', deleteTransError);
        return { error: deleteTransError, data: null };
      }
    }
    
    if (copyConfigs) {
      const { error: deleteConfigError } = await supabaseClient
        .from('configs')
        .delete()
        .eq('page_id', targetPageId);
      
      if (deleteConfigError) {
        console.error('Error deleting existing configs:', deleteConfigError);
        return { error: deleteConfigError, data: null };
      }
    }
  }
  
  // Copy translations and configs
  let translationsResult = { data: null, error: null };
  let configsResult = { data: null, error: null };
  
  if (copyTranslations) {
    translationsResult = await copyTranslationsFromPage(sourcePageId, targetPageId, translationFilters);
  }
  
  if (copyConfigs) {
    configsResult = await copyConfigsFromPage(sourcePageId, targetPageId, configFilters);
  }
  
  return {
    error: null,
    data: {
      pageFieldsCopied,
      translationsCopied: translationsResult.data?.length || 0,
      configsCopied: configsResult.data?.length || 0,
      translationsError: translationsResult.error,
      configsError: configsResult.error
    }
  };
}


export function emojifyLevel(level){
  if (!level) return "🚫";
  return {"reader": "👀", "editor": "✏️", "owner": "👑", "not invited": "🚫"}[level];
}
export function getSupaAccess({user, scope, resource}) {
  const supaACL = memo.supaACLForCurrentUser;
  if (!supaACL) return console.error("supaACL not loaded before checkSupaAccess call");
  const entry = supaACL.get(`${user}.${scope}.${resource}`) || supaACL.get(`${user}.${scope}.__all__`); ;
  return entry || "not invited";
}
function isLevelEnough(usersLevel, neededLevel){
  if (!usersLevel) return false; // acl record not found
  if (neededLevel === "reader") return usersLevel === "owner" || usersLevel === "editor" || usersLevel === "reader";
  if (neededLevel === "editor") return usersLevel === "owner" || usersLevel === "editor";
  if (neededLevel === "owner") return usersLevel === "owner";
}

export function checkSupaAccess({user, scope="dataset", resource, atLeast = "reader"}){
  const supaACL = memo.supaACLForCurrentUser;
  if (!supaACL) return console.error("supaACL not loaded before checkSupaAccess call"); 
  if (!resource) return false;
  if (scope === "server")
    return isLevelEnough(supaACL.get(`${user}.${scope}.${resource}`), atLeast) || isLevelEnough(supaACL.get(`${user}.${scope}.__all__`), atLeast);
  if (scope === "dataset")
    return isLevelEnough(supaACL.get(`${user}.${scope}.${resource}`), atLeast);
  if (scope === "site")
    return isLevelEnough(supaACL.get(`${user}.${scope}.${resource}`), atLeast);
  if (scope === "page")
    return isLevelEnough(supaACL.get(`${user}.${scope}.${resource}`), atLeast);
  return false;
}

